import { useEffect, useMemo, useRef, type RefObject } from "react"
import * as THREE from "three"

import { get_app_state } from "../../../state/store"
import { get_capacity_factor_mix } from "../../data/capacity_factor_data"
import { CapacityData } from "../../data/interface"
import pub_sub from "../../state/pub_sub"
import { IndicesMix } from "../../state/pub_sub/interface"


export function RenderCapacityFactorData(props: {
    coords: {
        // merged_fill: THREE.BufferGeometry | null
        // merged_outline: THREE.BufferGeometry | null
        cell_ids: string[]
        cell_vertex_ranges: { start: number; count: number }[]
    }
    merged_mesh_ref: RefObject<THREE.Mesh | null>
    shader_material: THREE.ShaderMaterial
    palettes: { wind: THREE.Vector4[]; solar: THREE.Vector4[] }
})
{
    const { coords, merged_mesh_ref, shader_material, palettes } = props

    const map_capacity_factors_source = get_app_state(state => state.view.map_capacity_factors_source)
    const map_capacity_factors_aggregation = get_app_state(state => state.view.map_capacity_factors_aggregation)
    const map_capacity_factors_discrete = get_app_state(state => state.view.map_capacity_factors_discrete)

    const wind_turbine_capacity_data = get_app_state(state => state.data.capacity_factor_data?.wind)
    const annual_wind_turbine_capacity_data = get_app_state(state => state.data.capacity_factor_data?.annual_wind)
    const solar_pv_capacity_data = get_app_state(state => state.data.capacity_factor_data?.solar)
    const annual_solar_pv_capacity_data = get_app_state(state => state.data.capacity_factor_data?.annual_solar)

    const capacity_data: CapacityData | undefined = useMemo(() =>
    {
        if (!map_capacity_factors_source) return undefined
        if (!wind_turbine_capacity_data) return undefined
        if (!annual_wind_turbine_capacity_data) return undefined
        if (!solar_pv_capacity_data) return undefined
        if (!annual_solar_pv_capacity_data) return undefined

        const capacity_data: CapacityData = {
            data: wind_turbine_capacity_data,
            type: "wind",
            display_type: map_capacity_factors_discrete ? "discrete" : "continuous",
        }

        if (map_capacity_factors_source === "wind")
        {
            capacity_data.data = map_capacity_factors_aggregation === "annual_average"
                ? annual_wind_turbine_capacity_data
                : wind_turbine_capacity_data
            return capacity_data
        }

        capacity_data.type = "solar"
        capacity_data.data = map_capacity_factors_aggregation === "annual_average"
            ? annual_solar_pv_capacity_data
            : solar_pv_capacity_data

        return capacity_data
    }, [
        map_capacity_factors_source, map_capacity_factors_aggregation,
        wind_turbine_capacity_data, annual_wind_turbine_capacity_data,
        solar_pv_capacity_data, annual_solar_pv_capacity_data,
    ])


    const animation_state_ref = useRef({
        last_animated_at_ms: -Infinity,
        animate_fps: 10,
    })

    useEffect(() =>
    {
        if (!capacity_data?.data)
        {
            // Clear the palette and set all palette indices to 0
            apply_capacity_palette_and_indices({
                mesh: merged_mesh_ref.current,
                coords,
                shader_material,
                palettes,
            })
            return
        }

        // Build an index -> ms array for fast lookup (ordered by index)
        const datetime_ms_by_index: number[] = new Array(capacity_data.data.date_time_to_index.size)
        capacity_data.data.date_time_to_index.forEach((idx, date_time) => {
            datetime_ms_by_index[idx] = Date.parse(date_time)
        })

        const state = animation_state_ref.current

        const unsub = pub_sub.sub("simulation_datetime", payload => {
            const now = performance.now()
            if (!state.animate_fps) return
            if ((now - state.last_animated_at_ms) < (1000 / state.animate_fps)) return
            state.last_animated_at_ms = now

            apply_capacity_palette_and_indices({
                mesh: merged_mesh_ref.current,
                coords,
                shader_material,
                palettes,
                capacity_data,
                datetime_indices_mix: payload.datetime_annual_hourly_indices_mix,
            })
        }, "H3Cells-sim")

        return unsub
    }, [capacity_data, coords.cell_ids, coords.cell_vertex_ranges, palettes, shader_material, merged_mesh_ref])

    return null
}



const DEFAULT_INDICES_MIX: IndicesMix = { one: 0, two: 0, mix: 0 }

function apply_capacity_palette_and_indices(params: {
    mesh: THREE.Mesh | null
    coords: { cell_ids: string[]; cell_vertex_ranges: { start: number; count: number }[] }
    shader_material: THREE.ShaderMaterial
    palettes: { wind: THREE.Vector4[]; solar: THREE.Vector4[] }
    capacity_data?: CapacityData | null
    datetime_indices_mix?: IndicesMix
}) {
    const {
        mesh,
        coords,
        shader_material,
        palettes,
        capacity_data,
        datetime_indices_mix = DEFAULT_INDICES_MIX,
    } = params

    if (!mesh) return
    const geom = mesh.geometry as THREE.BufferGeometry | null
    if (!geom || !coords.cell_ids || !coords.cell_vertex_ranges) return

    const attr = geom.getAttribute("palette_index") as THREE.BufferAttribute | undefined
    if (!attr) return
    const arr = attr.array as Float32Array

    const pad_palette = (p: THREE.Vector4[]) => {
        const out: THREE.Vector4[] = p.slice(0, 8).map(v => new THREE.Vector4(v.x, v.y, v.z, (v.w === undefined ? 1 : v.w)))
        while (out.length < 8) out.push(new THREE.Vector4(0, 0, 0, 0))
        return out
    }

    const data = capacity_data?.data
    if (!data)
    {
        const base = (palettes.wind && palettes.wind.length) ? palettes.wind : palettes.solar
        const clear_palette = pad_palette(base.map(v => new THREE.Vector4(v.x, v.y, v.z, 0)))
        ;(shader_material.uniforms as any).palette.value = clear_palette
        for (let i = 0; i < arr.length; i++) arr[i] = 0
        attr.needsUpdate = true
        return
    }

    const palette = capacity_data.type === "wind" ? palettes.wind : palettes.solar
    ;(shader_material.uniforms as any).palette.value = pad_palette(palette)

    const palette_count = Math.max(1, palette.length)
    coords.cell_ids.forEach((cell_id, i) => {
        const capacity_factor = get_capacity_factor_mix(data, datetime_indices_mix, cell_id) ?? 0
        const idxf = capacity_data.display_type === "continuous"
            ? capacity_factor * (palette_count - 1)
            : Math.round(capacity_factor * (palette_count - 1))

        const range = coords.cell_vertex_ranges[i]!
        const start = range.start
        const end = start + range.count
        for (let v = start; v < end; v++) arr[v] = idxf
    })

    attr.needsUpdate = true
}
